import { api } from ".";

export interface User {
    Id: number;
    EmailAddress: string;
    IsActive: boolean;
    RolesCount: number;
}

export interface CustomerSummary {
  Id: number;
  Name: string;
}

export interface UserWithCustomers {
  Id: number;
  EmailAddress: string;
  IsActive: boolean;
  Customers: CustomerSummary[];
}

export interface CustomerUser {
  Id: number;
  EmailAddress: string;
  TmsUsername: string;
  IsActive: boolean;
}

export interface TmsCustomerUser {
  TmsUsername: string;
  EmailAddress: string;
}

export interface TmsUserWithReason extends TmsCustomerUser {
  reason: string;
}

export const isValidEmail = (email: string): boolean => {
  // very basic email regex; tweak if you need stricter validation
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

export interface UserModel {
  EmailAddress: string;
}

export interface CustomerUsersMigrationModel {
  CustomerId: number;
  TmsCustomerUsers: TmsUserWithReason[];
  UseTmsPassword?: boolean;
}

export interface CustomerUserModel {
  CustomerId: number;
  EmailAddress: string;
  DisplayName: string;
  TmsUsername: string;
}

export interface UpdateCustomerUserModel {
  CustomerId: number;
  UserId: number;
  TmsUsername: string;
}

export interface ApplicationCustomerUserRoles {
  AppId: number;
  CustomerId: number;
  UserId: number;
  RoleIds: number[];
}

export interface UserAccessPrivilege {
  Id: number;
  Name: string;
  Description?: string;
  IsAssigned: boolean;
}

export interface UserAccessRole {
  Id: number;
  Name: string;
  Description?: string;
  PrivilegesCount: number;
  IsAssigned: boolean;
}

export interface UserAccessCustomer {
  Id: number;
  Name: string;
  Roles: UserAccessRole[];
  Privileges: UserAccessPrivilege[];
}

export interface UserAccessApplication {
  Id: number;
  Name: string;
  Customers: UserAccessCustomer[];
}

export interface UserAccessMap {
  UserId: number;
  EmailAddress: string;
  Applications: UserAccessApplication[];
}

const userUrlV1 = "/v1/Users";
const appCustomerUsersUrlV1 = "/v1/ApplicationCustomerUsers";
const appCustomerUserRolesUrlV1 = "/v1/ApplicationCustomerUserRoles";
const customerUsersUrlV1 = "/v1/CustomerUsers";

export const getUsersByApplicationAndCustomerId = async (appId: number, customerId: number) => {
  const response = await api.get<User[]>(`${appCustomerUsersUrlV1}/${appId}/customers/${customerId}/users`);
  return response?.data;
};

export const getUsersByCustomerId = async (customerId: number) => {
  const response = await api.get<CustomerUser[]>(`${customerUsersUrlV1}/${customerId}/users`);
  return response?.data;
};

export const getTmsUsersByCustomerId = async (customerId: number) => {
  const response = await api.get<TmsCustomerUser[]>(`${customerUsersUrlV1}/${customerId}/tms/users`);
  return response?.data;
};

export const addTmsUsersToCustomer = async (customerId: number, model: CustomerUsersMigrationModel) => {
  const response = await api.post(`${customerUsersUrlV1}/${customerId}/tms/users`, model);
  return response?.data;
};

export const addUserToCustomer = async (customerId: number, model: CustomerUserModel) => {
  const response = await api.post(`${customerUsersUrlV1}/${customerId}/user`, model);
  return response?.data;
};

export const removeUserFromCustomer = async (customerId: number, userId: number) => {
  const response = await api.delete(`${customerUsersUrlV1}/${customerId}/users/${userId}`);
  return response?.status === 204;
};

export const updateCustomerUser = async (
  customerId: number,
  userId: number,
  model: UpdateCustomerUserModel
) => {
  const response = await api.put(
    `${customerUsersUrlV1}/${customerId}/users/${userId}`,
    model
  );
  return response?.status === 204;
};

export const getAllActiveUsers = async () => {
  const response = await api.get<any>(userUrlV1);
  const data = response?.data;
  if (Array.isArray(data)) {
    return data as User[];
  }
  return (data?.Items || []) as User[];
};

export const getUsers = async (includeInactive: boolean = false) => {
  const response = await api.get<User[]>(userUrlV1, {
    params: { includeInactive },
  });
  return response?.data;
};

export type UserStatusFilter = "all" | "active" | "inactive" | "unassigned";
export type UserSortBy = "Id" | "EmailAddress";
export enum UserSortDirection {
  Ascending = 0,
  Descending = 1,
}

export interface PagedUserResponse {
  items: UserWithCustomers[];
  totalCount: number;
  pageSize: number;
  currentPage: number;
  totalPages: number;
}

export interface UsersQueryParams {
  statusFilter?: UserStatusFilter;
  emailSearch?: string;
  pageSize?: number;
  currentPage?: number;
  sortBy?: UserSortBy;
  sortDirection?: UserSortDirection;
}

export const getUsersPaged = async (params: UsersQueryParams = {}) => {
  const response = await api.get<any>(userUrlV1, { params });
  const data = response?.data;
  return {
    items: data?.Items || [],
    totalCount: data?.TotalCount || 0,
    pageSize: data?.PageSize || params.pageSize || 10,
    currentPage: data?.CurrentPage || params.currentPage || 1,
    totalPages: data?.TotalPages || 1,
  } as PagedUserResponse;
};

export const getUserAccessMap = async (userId: number) => {
  const response = await api.get<UserAccessMap>(`${userUrlV1}/${userId}/access`);
  return response?.data;
};

export const downloadUserAccessReport = async (userId: number) => {
  const response = await api.get(`${userUrlV1}/${userId}/access/report`, {
    responseType: "blob",
  });
  return response;
};

export const addUser = async (userModel: UserModel) => {
  const response = await api.post(userUrlV1, userModel);
  return response?.data;
}
export const deleteUser = async (userId: number) => {
  const response = await api.delete(`${userUrlV1}/${userId}`);
  return response?.status === 204;
};

export const reactivateUser = async (userId: number) => {
  const response = await api.post(`${userUrlV1}/${userId}/reactivate`, {});
  return response?.status === 204;
};

export const getUser = async (userId: number) => {
  const response = await api.get<User>(`${userUrlV1}/${userId}`);
  return response?.data;
};

export const addApplicationCustomerUserRoles = async (model: ApplicationCustomerUserRoles) => {
  const response = await api.post(`${appCustomerUserRolesUrlV1}/AddAccess`, model);
  return response?.data;
}

export const removeApplicationCustomerUserRoles = async (model: ApplicationCustomerUserRoles) => {
  const response = await api.post(`${appCustomerUserRolesUrlV1}/RemoveAccess`, model);
  return response?.data;
}
